import axios from "../../axios";
import Tag from "@/inside/models/Action/Tag";

let cache = null;

export default {
    actions: {
        /**
         * Creates a resource corresponding to given `tag` using Echoline API.
         *
         * @param {function} commit
         * @param {Array} rootGetters
         * @param {Tag} tag
         * @return {Promise<unknown>}
         */
        create({commit, rootGetters}, tag) {
            if (!(tag instanceof Tag)) {
                throw new TypeError(`VueX 'actions/tags/create' action requires payload to be an instance of Tag, ${typeof tag} given`);
            }

            const user = rootGetters["user/get"];
            return new Promise((resolve, reject) => {
                axios.post(`/accounts/${user.account.id}/actions/tags`, tag, {
                    validateStatus: (status => status === 201)
                }).then((response) => {
                    const tag = Tag.fromAPI(response.data);
                    commit("add", tag);
                    resolve(tag);
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Deletes given `tag` against Echoline API.
         *
         * @param {function} commit
         * @param {Tag} tag
         * @return {Promise<unknown>}
         */
        delete({commit}, tag) {
            if (!(tag instanceof Tag)) {
                throw new TypeError(`VueX 'actions/tags/delete' action expects payload to be an instance of Tag, ${typeof tag} given`);
            }

            if (tag.id === null) {
                throw new Error("Tag ID must be set");
            }

            return new Promise((resolve, reject) => {
                axios.delete(`/actions/tags/${tag.id}`, tag, {
                    validateStatus: (status => status === 200)
                }).then(() => {
                    commit("remove", tag);
                    resolve();
                }).catch((error) => {
                    reject(error);
                });
            });
        },
        /**
         * Fetches all action tags for current user account.
         *
         * @param {function} commit
         * @param {Array} rootGetters
         * @param {boolean} force_refresh
         * @return {Promise}
         */
        fetch({commit, rootGetters}, force_refresh) {
            if (force_refresh === undefined) {
                force_refresh = false;
            } else if (typeof force_refresh !== "boolean") {
                throw new TypeError(`VueX 'actions/tags/fetch' action expects payload to be of type boolean, ${typeof force_refresh} given`);
            }

            const user = rootGetters["user/get"];

            if (!cache || force_refresh) {
                cache = axios.get(`/accounts/${user.account.id}/actions/tags`, {
                    validateStatus: (status => status === 200)
                }).then((response) => {
                    response.data.forEach((tag_data) => {
                        commit("add", Tag.fromAPI(tag_data));
                    });
                });
            }

            return cache;
        },
        /**
         * Updates given `tag` against Echoline API.
         *
         * @param {function} commit
         * @param {Tag} tag
         * @return {Promise}
         */
        update({commit}, tag) {
            if (!(tag instanceof Tag)) {
                throw new TypeError(`VueX 'actions/tags/update' action expects payload to be an instance of Tag, ${typeof tag} given`);
            }

            if (tag.id === null) {
                throw new Error("Tag ID must be set");
            }

            return new Promise((resolve, reject) => {
                axios.put(`/actions/tags/${tag.id}`, tag, {
                    validateStatus: (status => status === 200)
                }).then(() => {
                    commit("add", tag);
                    resolve(tag);
                }).catch((error) => {
                    reject(error);
                });
            });
        }
    },
    getters: {
        /**
         * Returns all tags currently stored in module state.
         *
         * @param state
         * @returns {Tag[]}
         */
        all(state) {
            return state.tags;
        }
    },
    mutations: {
        /**
         * Adds given `tag` to module state. If given `tag` ID matches with an existing tag in module state, existing
         * tag is replaced by given `tag`.
         *
         * @param state
         * @param {Tag} tag
         */
        add(state, tag) {
            if (!(tag instanceof Tag)) {
                throw new TypeError(`VueX 'actions/tags/set' mutation expects payload to be an instance of Tag, ${typeof tag} given`);
            }

            if (tag.id === null) {
                throw new Error("Tag ID must be set");
            }

            let found = false;
            state.tags = state.tags.map((existing_tag) => {
                if (existing_tag.id === tag.id) {
                    found = true;
                    existing_tag.label = tag.label;
                    existing_tag.color = tag.color;
                }

                return existing_tag;
            });

            if (!found) {
                state.tags.push(tag);
            }
        },
        /**
         * Removes given `tag` from module state.
         *
         * @param state
         * @param {Tag} tag
         */
        remove(state, tag) {
            if (!(tag instanceof Tag)) {
                throw new TypeError(`VueX 'actions/tags/remove' mutation expects payload to be an instance of Tag, ${typeof tag} given`);
            }

            if (tag.id === null) {
                throw new Error("Tag ID must be set");
            }

            state.tags = state.tags.filter((existing_tag) => {
                return existing_tag.id !== tag.id;
            });
        },
    },
    namespaced: true,
    state: {
        tags: []
    }
}
